import { UseTranslationResponse } from 'react-i18next';
import { IColumnProps } from 'devextreme-react/data-grid';
import clsx from 'clsx';
import formStyles from '../../../../../components/form/form.module.scss';
import styles from '../../SNLots.module.scss';
import { formatDate } from '../../../../../helpers/abas-helper.ts';
import { Button } from '../../../../../components/uikit/buttons/Button.tsx';
import React from 'react';
import { LottieContainer } from '../../../../../components/animations/LottieContainer.tsx';
import animationData from '../../../../../assets/animations/loader_small.json';
import { downloadOrOpenBlobFile } from '../../../../../helpers/download-helper.ts';
import { TrackEventParams } from '@jonkoops/matomo-tracker-react/lib/types';
import { SNLotsCategory } from '../../SNLots.tsx';
import { Tip } from '../../../../../components/uikit/Tip.tsx';

export const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.SNLots'>,
  i18nCommon: UseTranslationResponse<'translation', undefined>,
  addProcessingLink: (link: string) => void,
  removeProcessingLink: (link: string) => void,
  processingLinks: Set<string>,
  showTooltip: () => void,
  trackEvent: (params: TrackEventParams) => void | undefined,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'number',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('number'),
    cssClass: clsx(
      formStyles.textCellPadding,
      styles.textAlignLeft,
      formStyles.tableHeaderPointer,
    ),
    minWidth: 200,
  },
  {
    dataField: 'type',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('type'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 200,
  },
  {
    dataField: 'validFrom',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('valid_from'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 200,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: 'link',
    allowEditing: false,
    allowSorting: false,
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, styles.buttonPadding),
    width: 136,
    cellRender: (cell) => {
      const none = 'none';
      const filePath = cell.value;
      const extension = filePath.slice(filePath.lastIndexOf('.') + 1);

      const fileName = cell.data.number;

      const link = cell.value;
      const isLoading = processingLinks.has(link);

      if (cell.value === none) {
        return (
          <Tip
            tooltipText={i18nCommon.t('link_unavailable_msg')}
            textBody={
              <div
                data-test-id={'disabled-download-button'}
                className={styles.disabledButton}
              >
                {i18n.t('download')}
              </div>
            }
            className={styles.tip}
            tooltipClassName={styles.margins}
          />
        );
      }

      return (
        <>
          {isLoading ? (
            <LottieContainer
              animationData={animationData}
              segments={isLoading ? [1, 180] : [180, 270]}
              loop={isLoading}
              width={28}
              height={28}
            />
          ) : (
            <div>
              <Button
                testId={'download-button'}
                className={clsx(styles.actionButton)}
                title={i18n.t('download')}
                onClick={() => {
                  trackEvent({
                    category: SNLotsCategory,
                    action: 'Download file',
                    name: link,
                  });
                  addProcessingLink(link);
                  downloadOrOpenBlobFile(filePath, fileName)
                    .catch(showTooltip)
                    .finally(() => removeProcessingLink(link));
                }}
              />
              <div className={styles.caption}>{extension}</div>
            </div>
          )}
        </>
      );
    },
  },
];

export const defineType = (typeOfSerialNumber: string | null | undefined) => {
  switch (typeOfSerialNumber) {
    case 'delivery_note':
    case 'invoice':
      return typeOfSerialNumber.replace('_', ' ');
    case 'other':
      return 'unknown';
    case 'all':
    default:
      return undefined;
  }
};
